// Constants
import { AppContext } from "@ms/uno-constants/lib/local/configuration/AppContext";
import { DefaultOrderHint } from "@ms/uno-constants/lib/local/OrderHintConstants";
import { CopilotWorkflowServiceReason, CopilotWorkflowServiceState, CopilotWorkflowUnifiedState } from "@ms/uno-constants/lib/local/WorkflowConstants";
import { Temporal } from "temporal-polyfill-lite";
import { ContentLinkType, TaskStatus } from "../../../service/planner/enumerations/IPlannerEnumerations";
import { TaskExtensionKeys, TaskPersonalViewExtensionKeys, TaskPlanViewExtensionKeys } from "../../../service/planner/extensions/ITaskExtensionsResource";
import { PriorityName } from "../Enumerations";
import { UserAssignment } from "../assignment/UserAssignment";
import { ChecklistItem } from "../checklistItem/ChecklistItem";
import { DateWithOptionalTime } from "../common/DateWithOptionalTime";
import { ContentType } from "../common/IItemBody";
import { PatchableAndDeltaSyncableODataEntity } from "../common/PatchableAndDeltaSyncableODataEntity";
import { ContentLink } from "../contentLink/ContentLink";
import { RecurrenceOccurrence } from "../recurrence/RecurrenceOccurrence";
import { GanttTaskType } from "./Enumerations";
import { TaskKind, TaskPreviewType } from "./ITask";
import { TaskPersonalViewOrderHints } from "./TaskPersonalViewOrderHints";
import { TaskPlanViewOrderHints } from "./TaskPlanViewOrderHints";
// Utilities
import { ErrorUtilities } from "@ms/uno-errors/lib/local/utilities/ErrorUtilities";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { getTimeZone } from "@ms/uno-utilities/lib/local/CalendarUtilities";
import { compareByTemporalDate, compareTemporalDate } from "@ms/uno-utilities/lib/local/Comparators";
import { computeConstrainedDates } from "../../../utilities/DateConstraintUtils";
import { getCurrentDateWithoutTimestamp } from "@ms/uno-utilities/lib/local/DateUtilities";
import { LocalIdGenerator } from "@ms/uno-utilities/lib/local/LocalIdGenerator";
import { getDiff, getDiffTemporalCustomizer } from "@ms/uno-utilities/lib/local/ObjectUtilities";
import cloneDeep from "lodash/cloneDeep";
import isEmpty from "lodash/isEmpty";
import isNumber from "lodash/isNumber";
import { ConvertPriorityNameToPriority, ConvertPriorityToPriorityName } from "../../../utilities/ConversionUtilities";
import { deserializeNotes, serializeNotes } from "../../../utilities/SerializationUtilities";
import { createTempRecurrenceOccurrence } from "../../../utilities/TaskUtilities";
import { customFieldValueToStorageString } from "../customFields/coerceCustomFieldStringValue";
import { CustomFieldValue } from "../customFields/CustomFieldValue";
import { calculateDueDateFromSchedule } from "../recurrence/utilities/RecurrenceCalculatorUtilities";
/**
 * The task entity implementation.
 */ export class Task extends PatchableAndDeltaSyncableODataEntity {
    getPropertyMappingOverride() {
        return {
            checklist: "checklist@delta",
            customFieldValues: "customFieldValues@delta",
            links: "links@delta",
            userAssignments: "userAssignments@delta"
        };
    }
    mapEntityChangesToResource(changed, source) {
        const resourceKeyMap = this.getChangePropertyMapping();
        return {
            [resourceKeyMap.bucketId]: changed.bucketId,
            [resourceKeyMap.checklist]: this.processSubEntityArray(this.checklist, source.checklist),
            [resourceKeyMap.customFieldValues]: this.processSubEntityArray(this.customFieldValues, source.customFieldValues),
            [resourceKeyMap.displayName]: changed.displayName,
            // Use ternary to handle the three cases:
            // 1. undefined -> stays undefined (not included in update)
            // 2. null -> explicitly send null (to clear the field)
            // 3. actual value -> call forUpdate() on it
            [resourceKeyMap.dueDateTime]: changed.dueDateTime === undefined ? undefined : changed.dueDateTime?.forUpdate() ?? null,
            [resourceKeyMap.extensions]: changed.extensions && this.serializeExtensions(),
            [resourceKeyMap.finishDateTime]: changed.finishDateTime === undefined ? undefined : changed.finishDateTime?.forUpdate() ?? null,
            [resourceKeyMap.goalIds]: changed.goalIds,
            [resourceKeyMap.links]: this.processSubEntityArray(this.links, source.links),
            [resourceKeyMap.notes]: changed.notes && serializeNotes(changed.notes),
            [resourceKeyMap.parentTaskId]: changed.parentTaskId,
            [resourceKeyMap.percentComplete]: changed.percentComplete,
            [resourceKeyMap.personalView]: changed.personalView && this.serializePersonalViewForUpdate(source.personalView, changed.personalView),
            [resourceKeyMap.planView]: changed.planView && this.serializePlanViewForUpdate(source.planView, changed.planView),
            [resourceKeyMap.priority]: changed.priority,
            [resourceKeyMap.startDateTime]: changed.startDateTime === undefined ? undefined : changed.startDateTime?.forUpdate() ?? null,
            [resourceKeyMap.userAssignments]: this.processSubEntityArray(this.userAssignments, source.userAssignments),
            [resourceKeyMap.recurrence]: (changed.recurrence && this.recurrence?.forUpdate(source.recurrence)) ?? undefined
        };
    }
    mapDeltaChangesToEntity(delta) {
        return this.processDeltaWithTranslators(delta, {
            archivalInfo: (deltaValue)=>({
                    ...deltaValue,
                    modifiedDate: deltaValue.modifiedDate ? Temporal.PlainDate.from(deltaValue.modifiedDate) : null
                }),
            creationInfo: (deltaValue)=>({
                    ...deltaValue,
                    createdDateTime: Temporal.Instant.from(deltaValue.createdDateTime)
                }),
            checklist: (deltaValue)=>this.applySubEntityArrayDelta(deltaValue, this.checklist),
            customFieldValues: (deltaValue)=>this.applySubEntityArrayDelta(deltaValue, this.customFieldValues),
            dependencies: (deltaValue)=>deltaValue ? deltaValue.dependencies.map((dependency)=>dependency.id) : [],
            dueDateTime: (deltaValue)=>Task.deserializeDateWithOptionalTimeField(this.id, deltaValue, "DueDateTime"),
            extensions: (deltaValue)=>Task.deserializeExtensions(deltaValue),
            finishDateTime: (deltaValue)=>Task.deserializeDateWithOptionalTimeField(this.id, deltaValue, "FinishDateTime"),
            lastModifiedDateTime: (deltaValue)=>Temporal.Instant.from(deltaValue),
            links: (deltaValue)=>this.applySubEntityArrayDelta(deltaValue, this.links),
            notes: (deltaValue)=>deserializeNotes(deltaValue),
            personalView: (deltaValue)=>Task.deserializePersonalView({
                    isOnMyDay: deltaValue.isOnMyDay ?? this.personalView.isOnMyDay,
                    extensions: deltaValue.extensions ?? {}
                }),
            planView: (deltaValue)=>Task.deserializePlanView({
                    appliedCategoryIds: deltaValue.appliedCategoryIds ?? this.planView.appliedCategoryIds,
                    extensions: deltaValue.extensions ?? {}
                }),
            startDateTime: (deltaValue)=>Task.deserializeDateWithOptionalTimeField(this.id, deltaValue, "StartDateTime"),
            completedDateTime: (deltaValue)=>deltaValue ? Temporal.Instant.from(deltaValue) : null,
            recurrence: (deltaValue)=>deltaValue ? RecurrenceOccurrence.fromResource(deltaValue) : null,
            userAssignments: (deltaValue)=>this.applySubEntityArrayDelta(deltaValue, this.userAssignments)
        });
    }
    getDiff(target) {
        const taskDiffCustomizer = (source, target, key)=>{
            // Need special handling for dates
            if (key === "finishDateTime" || key === "dueDateTime" || key === "startDateTime") {
                // If both source and target are null, consider them equal
                if (source === null && target === null) {
                    return undefined;
                }
                if (source !== null && source !== undefined) {
                    const dateDiff = source.getDiff(target);
                    if (isEmpty(dateDiff)) {
                        return undefined;
                    }
                }
                return target;
            }
            return getDiffTemporalCustomizer(source, target, key);
        };
        return getDiff(this, target, taskDiffCustomizer, true);
    }
    static fromResource(resource) {
        return new Task({
            id: resource.id,
            version: resource["@etag"],
            archivalInfo: {
                isArchived: resource.archivalInfo.isArchived,
                justification: resource.archivalInfo.justification,
                modifiedBy: resource.archivalInfo.modifiedBy,
                modifiedDate: resource.archivalInfo.modifiedDate ? Temporal.PlainDate.from(resource.archivalInfo.modifiedDate) : null
            },
            capabilities: resource.capabilities,
            userAssignments: resource.userAssignments.map((assignment)=>UserAssignment.fromResource(assignment)),
            bucketId: resource.bucketId,
            checklist: resource.checklist.map((item)=>ChecklistItem.fromResource(item)),
            customFieldValues: resource.customFieldValues?.map((fieldValue)=>CustomFieldValue.fromResource(fieldValue)) ?? [],
            creationInfo: {
                createdBy: resource.creationInfo.createdBy,
                createdDateTime: Temporal.Instant.from(resource.creationInfo.createdDateTime),
                externalId: resource.creationInfo.externalId,
                externalVersion: resource.creationInfo.externalVersion,
                scenarioId: resource.creationInfo.scenarioId,
                scope: resource.creationInfo.scope,
                externalUrl: resource.creationInfo.externalUrl
            },
            dependencies: resource.dependencies ? resource.dependencies.map((dependency)=>dependency.id) : [],
            displayName: resource.displayName,
            dueDateTime: this.deserializeDateWithOptionalTimeField(resource.id, resource.dueDateTime, "DueDateTime"),
            extensions: this.deserializeExtensions(resource.extensions),
            finishDateTime: this.deserializeDateWithOptionalTimeField(resource.id, resource.finishDateTime, "FinishDateTime"),
            goalIds: resource.goalIds,
            hasChildren: resource.hasChildren,
            hasChat: resource.hasChat,
            kind: TaskKind.Planner,
            lastModifiedBy: resource.lastModifiedBy,
            lastModifiedDateTime: Temporal.Instant.from(resource.lastModifiedDateTime),
            links: resource.links.map((link)=>ContentLink.fromResource(link)),
            notes: deserializeNotes(resource.notes),
            parentTaskId: resource.parentTaskId,
            permissionSetId: resource.permissionSetId,
            planId: resource.planId,
            priority: resource.priority,
            personalView: this.deserializePersonalView(resource.personalView),
            planView: this.deserializePlanView(resource.planView),
            startDateTime: this.deserializeDateWithOptionalTimeField(resource.id, resource.startDateTime, "StartDateTime"),
            percentComplete: resource.percentComplete,
            completedBy: resource.completedBy,
            completedDateTime: resource.completedDateTime ? Temporal.Instant.from(resource.completedDateTime) : null,
            recurrence: resource.recurrence ? RecurrenceOccurrence.fromResource(resource.recurrence) : null
        });
    }
    static fromPersistedResource(resource) {
        return new Task({
            id: resource.id,
            version: resource["@etag"],
            archivalInfo: {
                isArchived: resource.archivalInfo.isArchived,
                justification: resource.archivalInfo.justification,
                modifiedBy: resource.archivalInfo.modifiedBy,
                modifiedDate: resource.archivalInfo.modifiedDate ? Temporal.PlainDate.from(resource.archivalInfo.modifiedDate) : null
            },
            userAssignments: resource.userAssignments.map((assignment)=>UserAssignment.fromResource(assignment)),
            bucketId: resource.bucketId,
            capabilities: resource.capabilities,
            checklist: resource.checklist.map((item)=>ChecklistItem.fromResource(item)),
            customFieldValues: resource.customFieldValues?.map((fieldValue)=>CustomFieldValue.fromResource(fieldValue)) ?? [],
            creationInfo: {
                createdBy: resource.creationInfo.createdBy,
                createdDateTime: Temporal.Instant.from(resource.creationInfo.createdDateTime),
                externalId: resource.creationInfo.externalId,
                externalVersion: resource.creationInfo.externalVersion,
                scenarioId: resource.creationInfo.scenarioId,
                scope: resource.creationInfo.scope,
                externalUrl: resource.creationInfo.externalUrl
            },
            dependencies: resource.dependencies,
            displayName: resource.displayName,
            dueDateTime: this.deserializeDateWithOptionalTimeField(resource.id, resource.dueDateTime, "DueDateTime"),
            extensions: this.deserializeExtensions(resource.extensions),
            finishDateTime: this.deserializeDateWithOptionalTimeField(resource.id, resource.finishDateTime, "FinishDateTime"),
            goalIds: resource.goalIds,
            hasChildren: resource.hasChildren,
            hasChat: resource.hasChat,
            kind: resource.kind,
            lastModifiedBy: resource.lastModifiedBy,
            lastModifiedDateTime: Temporal.Instant.from(resource.lastModifiedDateTime),
            links: resource.links.map((link)=>ContentLink.fromResource(link)),
            notes: deserializeNotes(resource.notes),
            parentTaskId: resource.parentTaskId,
            permissionSetId: resource.permissionSetId,
            planId: resource.planId,
            priority: resource.priority,
            personalView: this.deserializePersonalView(resource.personalView),
            planView: this.deserializePlanView(resource.planView),
            startDateTime: this.deserializeDateWithOptionalTimeField(resource.id, resource.startDateTime, "StartDateTime"),
            percentComplete: resource.percentComplete,
            completedBy: resource.completedBy,
            completedDateTime: resource.completedDateTime ? Temporal.Instant.from(resource.completedDateTime) : null,
            recurrence: resource.recurrence ? RecurrenceOccurrence.fromResource(resource.recurrence) : null
        });
    }
    /**
     * Get the priority string for the task
     * @param priority The priority value or PriorityName to get the string for
     */ static getPriorityText(priority) {
        const priorityName = isNumber(priority) ? ConvertPriorityToPriorityName(priority) : priority;
        switch(priorityName){
            case PriorityName.Low:
                return Strings.Task_PriorityLowText;
            case PriorityName.Important:
                return Strings.Task_PriorityImportantText;
            case PriorityName.Urgent:
                return Strings.Task_PriorityUrgentText;
            default:
            case PriorityName.Medium:
                return Strings.Task_PriorityMediumText;
        }
    }
    /**
     * Get the task status string for the task
     * @param status The status value or TaskStatus to get the string for
     */ static getTaskStatusText(status) {
        switch(status){
            case TaskStatus.NotStarted:
                return Strings.Task_NotStartedText;
            case TaskStatus.InProgress:
                return Strings.Task_InProgressText;
            case TaskStatus.Completed:
                return Strings.Task_CompletedText;
            default:
                ErrorUtilities.unreachableCase(status, ()=>{});
                throw new Error(`Invalid task status: ${status}`);
        }
    }
    /**
     * Deserializes the extensions of the task.
     * The values must be serializable to JSON.
     */ static deserializeExtensions(extensions) {
        const output = {
            conversationThreadId: null
        };
        // First parse the legacy data if it exists
        const serializedLegacyData = extensions[TaskExtensionKeys.legacyData];
        if (serializedLegacyData) {
            try {
                const legacyExtensionData = JSON.parse(serializedLegacyData);
                output.conversationThreadId = legacyExtensionData.conversationThreadId ?? null;
            } catch (parseError) {
                throw new Error(`Deserialization Error: [Entity=${this.name}][Field=Extensions][Key=legacyData]`);
            }
        }
        // Parse the copilot data if it exists
        const serializedCopilotData = extensions[TaskExtensionKeys.copilot];
        if (serializedCopilotData) {
            try {
                const copilotExtensionData = JSON.parse(serializedCopilotData);
                output.isAITask = copilotExtensionData.isAiTask;
            } catch (parseError) {
                throw new Error(`Deserialization Error: [Entity=${this.name}][Field=Extensions][Key=copilot]`);
            }
        }
        // Handle specific known extension keys
        for(const key in TaskExtensionKeys){
            const extensionKey = TaskExtensionKeys[key];
            // Skip legacyData and copilot as we handle them separately
            if (extensionKey === TaskExtensionKeys.legacyData || extensionKey === TaskExtensionKeys.copilot) {
                continue;
            }
            const serializedData = extensions[extensionKey];
            if (serializedData !== undefined) {
                try {
                    // Always try to parse as JSON since we always stringify during serialization
                    output[extensionKey] = JSON.parse(serializedData);
                } catch (parseError) {
                    throw new Error(`Deserialization Error: [Entity=${this.name}][Field=Extensions][Key=${extensionKey}]`);
                }
            }
        }
        return output;
    }
    /**
     * Deserializes the personal view of the task.
     * The values must be serializable to JSON.
     *
     * @param resource - The resource containing the personal view data.
     */ static deserializePersonalView(resource) {
        // Override with legacy data if present (takes precedence for overlapping fields)
        const legacyData = this.deserializePersonalViewLegacyData(resource.extensions);
        // Ensure we always have required fields with proper defaults
        const output = {
            isOnMyDay: resource.isOnMyDay,
            orderHints: legacyData.orderHints
        };
        // No custom extensions for personal view yet so just return the output based on legacy data
        return output;
    }
    /**
     * Deserializes legacy extensions that override Evo data for overlapping fields.
     * This method only handles the known legacy format fields for personal view.
     */ static deserializePersonalViewLegacyData(extensions) {
        const legacyOverrides = {};
        const serializedLegacyData = extensions[TaskPersonalViewExtensionKeys.legacyData];
        if (serializedLegacyData) {
            try {
                const legacyExtensionData = JSON.parse(serializedLegacyData);
                // Legacy only controls the main orderHints field
                legacyOverrides.orderHints = TaskPersonalViewOrderHints.fromLegacyData(legacyExtensionData.assigneePriority);
            } catch (parseError) {
                throw new Error(`Deserialization Error: [Entity=${this.name}][Field=PersonalViewExtensions][Key=legacyData]`);
            }
        }
        return legacyOverrides;
    }
    /**
     * Deserializes the plan view of the task.
     * The values must be serializable to JSON.
     *
     * @param resource - The resource containing the plan view data.
     */ static deserializePlanView(resource) {
        // Override with legacy data if present (takes precedence for overlapping fields)
        const legacyData = this.deserializePlanViewLegacyData(resource.extensions);
        // Ensure we always have required fields with proper defaults
        const output = {
            appliedCategoryIds: resource.appliedCategoryIds ?? [],
            previewType: legacyData.previewType,
            orderHints: legacyData.orderHints
        };
        // No custom extensions for plan view yet so just return the output based on legacy data
        return output;
    }
    /**
     * Deserializes legacy extensions that override Evo data for overlapping fields.
     * This method only handles the known legacy format fields.
     */ static deserializePlanViewLegacyData(extensions) {
        const legacyOverrides = {};
        const serializedLegacyData = extensions[TaskPlanViewExtensionKeys.legacyData];
        if (serializedLegacyData) {
            try {
                const legacyExtensionData = JSON.parse(serializedLegacyData);
                // Legacy only controls these specific fields
                legacyOverrides.previewType = legacyExtensionData.previewType || TaskPreviewType.Automatic;
                legacyOverrides.orderHints = TaskPlanViewOrderHints.fromLegacyData(legacyExtensionData.orderHint, legacyExtensionData.bucketTaskBoardFormatOrderHint, legacyExtensionData.progressTaskBoardFormatOrderHint, legacyExtensionData.assignedToTaskBoardFormatUnassignedOrderHint, legacyExtensionData.assignedToTaskBoardFormatAssigneeOrderHints);
            } catch (parseError) {
                throw new Error(`Deserialization Error: [Entity=${this.name}][Field=PlanViewExtensions][Key=legacyData]`);
            }
        }
        return legacyOverrides;
    }
    /**
     * Deserializes the date with optional time from the resource.
     * If the date is null, it returns null.
     */ static deserializeDateWithOptionalTimeField(id, date, fieldName) {
        if (!date) {
            return null;
        }
        try {
            return DateWithOptionalTime.fromResource(date);
        } catch (error) {
            throw new Error(`Deserialization Error: [Entity=${this.name}][Id=${id}][Field="${fieldName}"]`);
        }
    }
    get status() {
        if (this.isComplete()) {
            return TaskStatus.Completed;
        } else if (this.percentComplete === 0) {
            return TaskStatus.NotStarted;
        } else {
            return TaskStatus.InProgress;
        }
    }
    getClone() {
        return new Task({
            id: this.id,
            version: this.version,
            archivalInfo: cloneDeep(this.archivalInfo),
            capabilities: cloneDeep(this.capabilities),
            userAssignments: cloneDeep(this.userAssignments),
            bucketId: this.bucketId,
            checklist: cloneDeep(this.checklist),
            customFieldValues: cloneDeep(this.customFieldValues),
            creationInfo: cloneDeep(this.creationInfo),
            dependencies: cloneDeep(this.dependencies),
            displayName: this.displayName,
            dueDateTime: this.dueDateTime?.getClone() ?? null,
            extensions: cloneDeep(this.extensions),
            finishDateTime: this.finishDateTime?.getClone() ?? null,
            goalIds: cloneDeep(this.goalIds),
            hasChildren: this.hasChildren,
            hasChat: this.hasChat,
            kind: this.kind,
            lastModifiedBy: cloneDeep(this.lastModifiedBy),
            lastModifiedDateTime: Temporal.Instant.from(this.lastModifiedDateTime),
            links: cloneDeep(this.links),
            notes: cloneDeep(this.notes),
            parentTaskId: this.parentTaskId,
            permissionSetId: this.permissionSetId,
            planId: this.planId,
            priority: this.priority,
            personalView: cloneDeep(this.personalView),
            planView: cloneDeep(this.planView),
            startDateTime: this.startDateTime?.getClone() ?? null,
            percentComplete: this.percentComplete,
            completedBy: cloneDeep(this.completedBy),
            completedDateTime: this.completedDateTime ? Temporal.Instant.from(this.completedDateTime) : null,
            recurrence: this.recurrence ? this.recurrence.getClone() : null,
            timeZone: this.timeZone
        });
    }
    setProperty(key, value) {
        if (key === "status") {
            return this.setStatus(value);
        }
        const clone = this.getClone();
        if (key === "percentComplete" && this.percentComplete !== value) {
            // If the percent complete is set to 100, we complete the task
            if (value === 100) {
                return this.completeTask();
            } else {
                if (this.percentComplete === 100) {
                    // If the task was previously complete, we need to re-activate it
                    return this.reactivateTask();
                }
            // The rolldown (task -> assignment) estimation for a simple change to %complete will happen in the store as a store promotion
            }
        }
        // Handle start date, due date, and finish date change
        if (key === "startDateTime" || key === "dueDateTime" || key === "finishDateTime") {
            return this.setDateTimeWithValidation(key, value);
        }
        // Apply the property change
        if (key !== "status") {
            clone[key] = value;
        }
        return clone;
    }
    setNotes(content) {
        const cloneResource = this.getClone().forSerialization();
        cloneResource.notes = content ? {
            content,
            contentType: ContentType.HTML
        } : null;
        return Task.fromPersistedResource(cloneResource);
    }
    // Sets startDateTime and finishDateTime directly, bypassing the duration-preservation logic in
    // computeConstrainedDates (which would shift the other end to keep bar length constant). Used by
    // expander drags so one edge can move while the other stays fixed. dueDateTime is bumped when
    // the new start exceeds it to preserve the start <= due.
    setStartAndFinishDatesUnconstrained(newStart, newFinish) {
        const resource = this.forSerialization();
        resource.startDateTime = newStart?.forSerialization() ?? null;
        resource.finishDateTime = newFinish?.forSerialization() ?? null;
        // Bump dueDateTime if the new start exceeds it — the API enforces dueDateTime >= startDateTime.
        // Skipped when EnableIndependentTaskDueDate is on (same gate as setDateTimeWithValidation).
        const allowIndependentStartAndDueDates = AppContext?.appConfiguration?.flights?.EnableIndependentTaskDueDate === true;
        if (!allowIndependentStartAndDueDates && newStart && this.dueDateTime) {
            const cmp = compareByTemporalDate(newStart.temporal, this.dueDateTime.temporal, (date)=>date);
            if (cmp > 0) {
                resource.dueDateTime = newStart.forSerialization();
            }
        }
        return Task.fromPersistedResource(resource);
    }
    applyFinishDateConstraint(resource, changedField, value) {
        if (!AppContext?.appConfiguration?.flights?.EnableTimelineViewEvo) {
            return;
        }
        const constrained = computeConstrainedDates(this.startDateTime, this.finishDateTime, changedField, value);
        resource.startDateTime = constrained.startDateTime?.forSerialization() ?? null;
        resource.finishDateTime = constrained.finishDateTime?.forSerialization() ?? null;
    }
    setDateTimeWithValidation(key, value) {
        const resource = this.forSerialization();
        resource[key] = value?.forSerialization() ?? null;
        // When EnableIndependentTaskDueDate is on, basic plan tasks allow startDate and dueDate
        // to be set independently — skip the auto-adjustment between them. The finishDate
        // adjustment is preserved (it's a separate Gantt-style constraint, not a basic-plan one).
        const allowIndependentStartAndDueDates = AppContext?.appConfiguration?.flights?.EnableIndependentTaskDueDate === true;
        let didUpdateDueDateTime = false;
        if (key === "startDateTime") {
            // If start date comes after the due date, adjust due date to match
            if (!allowIndependentStartAndDueDates && value && this.dueDateTime) {
                const comparisonResult = compareByTemporalDate(value.temporal, this.dueDateTime.temporal, (date)=>{
                    return date;
                });
                if (comparisonResult > 0) {
                    resource.dueDateTime = value.forSerialization();
                    didUpdateDueDateTime = true;
                }
            }
            this.applyFinishDateConstraint(resource, "startDateTime", value);
        } else if (key === "dueDateTime") {
            didUpdateDueDateTime = true;
            // If due date comes before the start date, adjust start date to match
            if (!allowIndependentStartAndDueDates && value && this.startDateTime) {
                const comparisonResult = compareByTemporalDate(value.temporal, this.startDateTime.temporal, (date)=>{
                    return date;
                });
                if (comparisonResult < 0) {
                    resource.startDateTime = value.forSerialization();
                    this.applyFinishDateConstraint(resource, "startDateTime", value);
                }
            }
        } else if (key === "finishDateTime") {
            this.applyFinishDateConstraint(resource, "finishDateTime", value);
        }
        // If due date is being adjusted, update recurrence data as well if needed
        if (didUpdateDueDateTime && this.recurrence?.isActive()) {
            if (value == null) {
                // Clear recurrence if due date is being cleared
                resource.recurrence = this.recurrence.setRecurrenceSchedule(null).forSerialization();
            } else if (compareTemporalDate(value.toTemporalInstant(), this.recurrence.schedule.nextOccurrenceDateTime) >= 0) {
                // Update range.startDate to match dueDateTime if the dueDateTime is on or after the next occurrence
                resource.recurrence = this.recurrence.setRecurrenceSchedule(this.recurrence.schedule.setRecurrenceRange(this.recurrence.schedule.range.setStartDate(value.toTemporalInstant()))).forSerialization();
            }
        }
        return Task.fromPersistedResource(resource);
    }
    forCreate(navProps) {
        return {
            bucketId: this.bucketId,
            checklist: this.checklist.map((item)=>{
                return item.forCreate();
            }),
            customFieldValues: this.customFieldValues.map((fieldValue)=>fieldValue.forCreate()),
            dependencies: navProps?.dependencies?.map((dependency)=>dependency.forCreate()),
            displayName: this.displayName,
            dueDateTime: this.dueDateTime?.forCreate() ?? null,
            finishDateTime: this.finishDateTime?.forCreate() ?? null,
            goalIds: this.goalIds,
            links: this.links.map((link)=>link.forCreate()),
            parentTaskId: undefined,
            planId: this.planId,
            priority: this.priority,
            notes: serializeNotes(this.notes),
            percentComplete: this.percentComplete,
            extensions: this.serializeExtensions(),
            personalView: this.serializePersonalForCreate(),
            planView: this.serializePlanView(),
            startDateTime: this.startDateTime?.forCreate() ?? null,
            // [TODO:3044338] Service API isn't available yet — Enable when API available
            // timeZone: this.timeZone,
            userAssignments: this.userAssignments.map((assignment)=>assignment.forCreate()),
            recurrence: this.recurrence?.forCreate()
        };
    }
    forSerialization() {
        return {
            id: this.id,
            "@etag": this.version,
            archivalInfo: {
                isArchived: this.archivalInfo.isArchived,
                justification: this.archivalInfo.justification,
                modifiedBy: this.archivalInfo.modifiedBy,
                modifiedDate: this.archivalInfo.modifiedDate?.toString() ?? null
            },
            userAssignments: this.userAssignments.map((assignment)=>assignment.forSerialization()),
            bucketId: this.bucketId,
            capabilities: this.capabilities,
            checklist: this.checklist.map((item)=>item.forSerialization()),
            customFieldValues: this.customFieldValues.map((fieldValue)=>fieldValue.forSerialization()),
            creationInfo: {
                createdBy: this.creationInfo.createdBy,
                createdDateTime: this.creationInfo.createdDateTime.toString(),
                externalId: this.creationInfo.externalId,
                externalVersion: this.creationInfo.externalVersion,
                scenarioId: this.creationInfo.scenarioId,
                scope: this.creationInfo.scope,
                externalUrl: this.creationInfo.externalUrl
            },
            dependencies: this.dependencies,
            displayName: this.displayName,
            dueDateTime: this.dueDateTime?.forSerialization() ?? null,
            extensions: this.serializeExtensions(),
            finishDateTime: this.finishDateTime ? this.finishDateTime.forSerialization() : null,
            goalIds: this.goalIds,
            hasChildren: this.hasChildren,
            hasChat: this.hasChat,
            lastModifiedBy: this.lastModifiedBy,
            lastModifiedDateTime: this.lastModifiedDateTime.toString(),
            links: this.links.map((link)=>link.forSerialization()),
            notes: serializeNotes(this.notes),
            percentComplete: this.percentComplete,
            parentTaskId: this.parentTaskId,
            permissionSetId: this.permissionSetId,
            planId: this.planId,
            priority: this.priority,
            personalView: this.serializePersonalView(),
            planView: this.serializePlanView(),
            startDateTime: this.startDateTime ? this.startDateTime.forSerialization() : null,
            completedBy: this.completedBy,
            completedDateTime: this.completedDateTime ? this.completedDateTime.toString() : null,
            kind: this.kind,
            myDayDate: this.personalView.isOnMyDay ? getCurrentDateWithoutTimestamp() : "",
            assigneeIdentityIds: this.userAssignments ? this.userAssignments.map((assignment)=>assignment.user.id) : [],
            recurrence: this.recurrence ? this.recurrence.forSerialization() : null
        };
    }
    isComplete() {
        return this.percentComplete === 100;
    }
    isLate() {
        if (!this.dueDateTime || this.isComplete()) {
            return false;
        }
        // In EVO Wave1, we continue with UNO behavior where Task due date is only compared at date level (not time)
        // So, we'll convert our dueDateTime to user's time zone and compare only the date portion
        const userTimeZone = getTimeZone();
        const dueDateInUserTz = this.dueDateTime.temporal.withTimeZone(userTimeZone);
        const todayInUserTz = Temporal.Now.zonedDateTimeISO(userTimeZone);
        return Temporal.PlainDate.compare(dueDateInUserTz.toPlainDate(), todayInUserTz.toPlainDate()) === -1;
    }
    completeTask() {
        return this.updatePercentCompleteForStatus(TaskStatus.Completed);
    }
    reactivateTask(status = TaskStatus.NotStarted) {
        return this.updatePercentCompleteForStatus(status);
    }
    isEligibleForMyDay(currentUserId) {
        return this.getAssigneeIds().indexOf(currentUserId) > -1 || this.kind === TaskKind.ToDo;
    }
    doesUrlExistAsAttachment(url) {
        return this.links?.some((link)=>link.webUrl === url) ?? false;
    }
    getContentLinksSortedByDisplayName() {
        const linksList = [];
        // Map array to a list, filtering to only displayable content link types
        this.links.forEach((link)=>{
            if (Task.DisplayableContentLinkTypes.includes(link.linkType)) {
                linksList.push(link);
            }
        });
        // Sort by display name (case-insensitive)
        linksList.sort((link1, link2)=>{
            const displayName1 = link1.displayName?.toLowerCase() || "";
            const displayName2 = link2.displayName?.toLowerCase() || "";
            return displayName1.localeCompare(displayName2);
        });
        return linksList;
    }
    getPlanViewOrderHint(grouping, key) {
        return this.planView.orderHints.get(grouping, key);
    }
    setPlanViewOrderHint(grouping, value, key) {
        const updatedOrderHints = this.planView.orderHints.set(grouping, value, key);
        const updatedPlanView = {
            ...this.planView,
            orderHints: updatedOrderHints
        };
        // Create a new task directly since planView is not mutable via setProperty
        const clone = this.getClone();
        clone.planView = updatedPlanView;
        return clone;
    }
    getPersonalViewOrderHint(grouping) {
        return this.personalView.orderHints.get(grouping);
    }
    setPersonalViewOrderHint(grouping, value) {
        const updatedOrderHints = this.personalView.orderHints.set(grouping, value);
        const updatedPersonalView = {
            ...this.personalView,
            orderHints: updatedOrderHints
        };
        // Create a new task directly since personalView is not mutable via setProperty
        const clone = this.getClone();
        clone.personalView = updatedPersonalView;
        return clone;
    }
    getPersonalViewIsOnMyDay() {
        return this.personalView.isOnMyDay;
    }
    setPersonalViewIsOnMyDay(isOnMyDay) {
        const updatedPersonalView = {
            ...this.personalView,
            isOnMyDay: isOnMyDay
        };
        // Create a new task directly since personalView is not mutable via setProperty
        const clone = this.getClone();
        clone.personalView = updatedPersonalView;
        return clone;
    }
    getAppliedCategoryIds() {
        return this.planView.appliedCategoryIds;
    }
    setAppliedCategoryIds(ids) {
        const updatedPlanView = {
            ...this.planView,
            appliedCategoryIds: [
                ...ids
            ]
        };
        // Create a new task directly since planView is not mutable via setProperty
        const clone = this.getClone();
        clone.planView = updatedPlanView;
        return clone;
    }
    setCustomFieldValue(customFieldDefinitionId, value) {
        const existing = this.customFieldValues.find((v)=>v.customFieldDefinitionId === customFieldDefinitionId);
        let updated;
        if (value === null) {
            if (!existing) {
                return this;
            }
            updated = this.customFieldValues.filter((v)=>v.customFieldDefinitionId !== customFieldDefinitionId);
        } else {
            const stringValue = customFieldValueToStorageString(value);
            if (existing) {
                updated = this.customFieldValues.map((v)=>v.customFieldDefinitionId === customFieldDefinitionId ? v.setProperty("value", stringValue) : v);
            } else {
                const newCfv = new CustomFieldValue({
                    id: LocalIdGenerator.generate(),
                    version: "1",
                    customFieldDefinitionId,
                    value: stringValue
                });
                updated = [
                    ...this.customFieldValues,
                    newCfv
                ];
            }
        }
        return this.setProperty("customFieldValues", updated);
    }
    setPlanViewPreviewType(previewType) {
        const updatedPlanView = {
            ...this.planView,
            previewType: previewType
        };
        // Create a new task directly since planView is not mutable via setProperty
        const clone = this.getClone();
        clone.planView = updatedPlanView;
        return clone;
    }
    setConversationThreadId(conversationThreadId) {
        const clone = this.getClone();
        clone.extensions = {
            ...this.extensions,
            conversationThreadId: conversationThreadId
        };
        return clone;
    }
    setRecurrenceSchedule(schedule) {
        const resource = this.forSerialization();
        // Early null check for schedule termination
        if (schedule == null) {
            if (this.recurrence != null) {
                // Terminate existing recurrence
                resource.recurrence = this.recurrence.setRecurrenceSchedule(null).forSerialization();
            }
            return Task.fromPersistedResource(resource);
        }
        // Schedule is being created, recreated, or updated
        const newRangeStartDate = calculateDueDateFromSchedule(schedule, this.dueDateTime ?? this.startDateTime ?? DateWithOptionalTime.fromPlainDate(Temporal.Now.plainDateISO()));
        const newScheduleWithUpdatedRange = schedule.setRecurrenceRange(schedule.range.setStartDate(newRangeStartDate.toTemporalInstant()));
        const recurrence = this.recurrence != null ? this.recurrence.setRecurrenceSchedule(newScheduleWithUpdatedRange) // Update or recreate
         : createTempRecurrenceOccurrence(this, newScheduleWithUpdatedRange); // Create
        resource.recurrence = recurrence.forSerialization();
        resource.dueDateTime = newRangeStartDate.forSerialization();
        return Task.fromPersistedResource(resource);
    }
    getAssigneeIds() {
        return this.userAssignments.map((assignment)=>assignment.user.id);
    }
    isAssignedToCopilotAgent() {
        const copilotWorkflowState = this.getCopilotWorkflowState();
        return copilotWorkflowState === CopilotWorkflowUnifiedState.InProgress || copilotWorkflowState === CopilotWorkflowUnifiedState.Queued;
    }
    getCopilotWorkflowState() {
        const copilotWorkflowState = this.extensions[TaskExtensionKeys.marsCopilotProperties]?.copilotWorkflowState;
        const reason = this.extensions[TaskExtensionKeys.marsCopilotProperties]?.reason;
        switch(copilotWorkflowState){
            case CopilotWorkflowServiceState.Accepted:
                return CopilotWorkflowUnifiedState.Queued;
            case CopilotWorkflowServiceState.Active:
                return CopilotWorkflowUnifiedState.InProgress;
            case CopilotWorkflowServiceState.Blocked:
                if (reason === CopilotWorkflowServiceReason.Error) {
                    return CopilotWorkflowUnifiedState.Error;
                } else if (reason === CopilotWorkflowServiceReason.NeedsInfo) {
                    return CopilotWorkflowUnifiedState.NeedsInfo;
                }
                return CopilotWorkflowUnifiedState.Unassigned;
            case CopilotWorkflowServiceState.Done:
                return CopilotWorkflowUnifiedState.Done;
            case CopilotWorkflowServiceState.Cancelled:
            default:
                return CopilotWorkflowUnifiedState.Unassigned;
        }
    }
    hasCompletedAgentOutput() {
        const state = this.getCopilotWorkflowState();
        return state === CopilotWorkflowUnifiedState.Done || state === CopilotWorkflowUnifiedState.NeedsInfo || state === CopilotWorkflowUnifiedState.Error;
    }
    getCopilotWorkflowCategory() {
        const unifiedState = this.getCopilotWorkflowState();
        // Map the 6 unified states to the 4 board column categories
        switch(unifiedState){
            case CopilotWorkflowUnifiedState.Queued:
            case CopilotWorkflowUnifiedState.InProgress:
                // Both Queued and InProgress map to InProgress column
                return CopilotWorkflowUnifiedState.InProgress;
            case CopilotWorkflowUnifiedState.Error:
            case CopilotWorkflowUnifiedState.NeedsInfo:
                // Both Error and NeedsInfo map to NeedsInfo column
                return CopilotWorkflowUnifiedState.NeedsInfo;
            case CopilotWorkflowUnifiedState.Done:
                return CopilotWorkflowUnifiedState.Done;
            case CopilotWorkflowUnifiedState.Unassigned:
            default:
                return CopilotWorkflowUnifiedState.Unassigned;
        }
    }
    getGanttTaskType() {
        if (this.hasChildren) {
            return GanttTaskType.Summary;
        }
        return GanttTaskType.Bar;
    }
    updatePercentCompleteForStatus(newStatus) {
        let newPercentComplete = 0;
        switch(newStatus){
            case TaskStatus.NotStarted:
                newPercentComplete = 0;
                break;
            case TaskStatus.InProgress:
                newPercentComplete = 50;
                break;
            case TaskStatus.Completed:
                newPercentComplete = 100;
                break;
            default:
                ErrorUtilities.unreachableCase(newStatus, ()=>{});
                newPercentComplete = 0;
        }
        const resource = this.forSerialization();
        resource.percentComplete = newPercentComplete;
        resource.userAssignments.forEach((ua)=>{
            ua.percentComplete = newPercentComplete;
        });
        return Task.fromPersistedResource(resource);
    }
    /**
     * Serializes the extensions of the task.
     * The values must be serializable to JSON.
     */ serializeExtensions() {
        const output = {};
        // Always serialize to both Legacy and Evo formats to support round-trip deserialization across v3 and v4 apis
        const legacyData = {
            conversationThreadId: this.extensions.conversationThreadId
        };
        output[TaskExtensionKeys.legacyData] = JSON.stringify(legacyData);
        // Serialize copilot data if isAITask is defined
        // This requires special handling because deserialization maps ITaskCopilotData.isAiTask to extensions.isAITask
        if (this.extensions.isAITask !== undefined) {
            const copilotData = {
                isAiTask: this.extensions.isAITask
            };
            output[TaskExtensionKeys.copilot] = JSON.stringify(copilotData);
        }
        for(const key in TaskExtensionKeys){
            // Serialize to Evo format - serialize all extension keys except legacyData and copilot
            const extensionKey = TaskExtensionKeys[key];
            // Skip legacyData and copilot as we handle them separately
            if (extensionKey === TaskExtensionKeys.legacyData || extensionKey === TaskExtensionKeys.copilot) {
                continue;
            }
            const value = this.extensions[extensionKey];
            if (value !== undefined) {
                // Always JSON stringify - the server expects string values in the extensions record
                output[extensionKey] = JSON.stringify(value);
            }
        }
        return output;
    }
    /**
     * Serializes the personal view of the task for update.
     * @param sourcePersonalView The original personal view before changes
     * @param personalViewChanges The changes to apply to the personal view
     */ serializePersonalViewForUpdate(sourcePersonalView, personalViewChanges) {
        const personalViewUpdateResource = {};
        // Handle legacy data changes
        let legacyData = {};
        if (personalViewChanges.orderHints) {
            const orderHintsDiff = getDiff(sourcePersonalView.orderHints.forSerialization(), this.personalView.orderHints.forSerialization(), getDiffTemporalCustomizer, true);
            if (!isEmpty(orderHintsDiff)) {
                legacyData = orderHintsDiff;
            }
        }
        // Serialize legacy data if we have any changes
        if (!isEmpty(legacyData)) {
            const extensions = {
                [TaskPersonalViewExtensionKeys.legacyData]: JSON.stringify(legacyData)
            };
            personalViewUpdateResource.extensions = extensions;
        }
        if (personalViewChanges.isOnMyDay !== undefined) {
            personalViewUpdateResource.isOnMyDay = personalViewChanges.isOnMyDay;
        }
        return personalViewUpdateResource;
    }
    /**
     * Serializes the personal view of the task.
     * The values must be serializable to JSON.
     */ serializePersonalView() {
        const extensions = {};
        // Serialize Legacy Data
        const legacyData = {
            ...this.personalView.orderHints.forSerialization()
        };
        extensions[TaskPersonalViewExtensionKeys.legacyData] = JSON.stringify(legacyData);
        return {
            isOnMyDay: this.personalView.isOnMyDay,
            extensions
        };
    }
    /**
     * Serializes the personal view of the task only if it has non-default values.
     * Currently Service has a bug where it'll "auto-assign" Task to current user if personalView is sent and Task isn't assigned to current user.
     * If personalView has non-default values, then it's done by UI that has already checked user assignment
     * TODO#2786888: Remove this conditional serialize when Service bug is fixed (or just remove this comment)
     */ serializePersonalForCreate() {
        const hasDefaultIsOnMyDay = this.personalView.isOnMyDay === false;
        const hasDefaultOrderHint = this.personalView.orderHints.forSerialization().assigneePriority === DefaultOrderHint;
        // If all values are default, return undefined
        if (hasDefaultIsOnMyDay && hasDefaultOrderHint) {
            return undefined;
        }
        // Otherwise, serialize the personal view
        return this.serializePersonalView();
    }
    /**
     * Serializes the plan view of the task.
     * The values must be serializable to JSON.
     */ serializePlanView() {
        // Serialize to Legacy format
        const legacyData = {
            ...this.planView.orderHints.forSerialization(),
            previewType: this.planView.previewType
        };
        const extensions = this.serializePlanViewLegacyExtensions(legacyData);
        const result = {
            appliedCategoryIds: this.planView.appliedCategoryIds,
            extensions
        };
        return result;
    }
    /**
     * Serializes planView extensions
     * @param planView The plan view to serialize extensions for
     */ serializePlanViewLegacyExtensions(properties) {
        if (isEmpty(properties)) {
            return {};
        }
        const extensions = {
            // Serialize Legacy Data
            [TaskPlanViewExtensionKeys.legacyData]: JSON.stringify(properties)
        };
        return extensions;
    }
    /**
     * Serializes the plan view of the task for update.
     * @param sourcePlanView The original plan view before changes
     * @param planViewChanges The changes to apply to the plan view
     */ serializePlanViewForUpdate(sourcePlanView, planViewChanges) {
        const planViewUpdateResource = {};
        // Handle legacy data changes
        let legacyData = {};
        if (planViewChanges.orderHints) {
            const orderHintsDiff = getDiff(sourcePlanView.orderHints.forSerialization(), this.planView.orderHints.forSerialization(), getDiffTemporalCustomizer, true);
            if (!isEmpty(orderHintsDiff)) {
                legacyData = orderHintsDiff;
            }
        }
        if (planViewChanges.previewType !== undefined) {
            legacyData.previewType = planViewChanges.previewType;
        }
        // Serialize legacy data if we have any changes
        if (!isEmpty(legacyData)) {
            planViewUpdateResource.extensions = this.serializePlanViewLegacyExtensions(legacyData);
        }
        if (planViewChanges.appliedCategoryIds !== undefined) {
            planViewUpdateResource.appliedCategoryIds = planViewChanges.appliedCategoryIds;
        }
        return planViewUpdateResource;
    }
    /**
     * Sets the status of the task
     * @param status The new status to set
     */ setStatus(status) {
        switch(status){
            case TaskStatus.NotStarted:
            case TaskStatus.InProgress:
                // If the task was previously complete, we need to re-activate it
                if (this.percentComplete === 100) {
                    return this.reactivateTask(status);
                }
                // If status changed, need to update percent complete accordingly
                if (this.status !== status) {
                    return this.updatePercentCompleteForStatus(status);
                }
                // No change in status, return copy of the same task
                const resource = this.forSerialization();
                return Task.fromPersistedResource(resource);
            case TaskStatus.Completed:
                return this.completeTask();
            default:
                ErrorUtilities.unreachableCase(status, ()=>{});
                throw new Error(`Invalid task status: ${status}`);
        }
    }
    getContentLinkWithLowestPreviewPriority() {
        // Filter to only previewable content link types that have previewPriority
        const referenceLinks = this.links.filter((link)=>{
            return Task.PreviewableContentLinkTypes.includes(link.linkType) && link.extensions.previewPriority != null;
        });
        if (referenceLinks.length === 0) {
            return null;
        }
        // Find the link with the lowest preview priority (numerically smallest)
        return referenceLinks.reduce((lowest, current)=>{
            const currentPriority = current.extensions.previewPriority ?? "";
            const lowestPriority = lowest.extensions.previewPriority ?? "";
            return currentPriority < lowestPriority ? current : lowest;
        });
    }
    getContentLinkWithHighestPreviewPriority() {
        // Filter to only previewable content link types that have previewPriority
        const referenceLinks = this.links.filter((link)=>{
            return Task.PreviewableContentLinkTypes.includes(link.linkType) && link.extensions.previewPriority != null;
        });
        if (referenceLinks.length === 0) {
            return null;
        }
        // Find the link with the highest preview priority (numerically largest)
        return referenceLinks.reduce((highest, current)=>{
            const currentPriority = current.extensions.previewPriority ?? "";
            const highestPriority = highest.extensions.previewPriority ?? "";
            return currentPriority > highestPriority ? current : highest;
        });
    }
    getPreview() {
        const previewType = this.planView.previewType;
        switch(previewType){
            case TaskPreviewType.Description:
                return this.getDescriptionPreview();
            case TaskPreviewType.CheckList:
                return this.getChecklistPreview();
            case TaskPreviewType.Reference:
                return this.getReferencePreview();
            case TaskPreviewType.Automatic:
                return this.getAutomaticPreview();
            case TaskPreviewType.NoPreview:
                return null;
            default:
                ErrorUtilities.unreachableCase(previewType, ()=>{});
                return null;
        }
    }
    /**
     * Gets the description/notes preview content
     */ getDescriptionPreview() {
        if (!this.notes?.content) {
            return null;
        }
        return {
            type: TaskPreviewType.Description,
            content: this.notes
        };
    }
    /**
     * Gets the checklist preview content
     */ getChecklistPreview() {
        if (!this.checklist || this.checklist.length === 0) {
            return null;
        }
        return {
            type: TaskPreviewType.CheckList,
            content: this.checklist
        };
    }
    /**
     * Gets the reference preview content
     */ getReferencePreview() {
        const previewLink = this.getContentLinkForPreview();
        if (!previewLink) {
            return null;
        }
        return {
            type: TaskPreviewType.Reference,
            content: previewLink
        };
    }
    /**
     * Gets the automatic preview content based on priority:
     * 1. Reference/Attachment (highest priority)
     * 2. Checklist
     * 3. Description/Notes (lowest priority)
     * @returns The first available preview content, or null if none exist
     */ getAutomaticPreview() {
        // Priority 1: Try to get reference(attachments) preview
        const referencePreview = this.getReferencePreview();
        if (referencePreview) {
            return referencePreview;
        }
        // Priority 2: Try to get checklist preview
        const checklistPreview = this.getChecklistPreview();
        if (checklistPreview) {
            return checklistPreview;
        }
        // Priority 3: Try to get description(notes) preview
        const descriptionPreview = this.getDescriptionPreview();
        if (descriptionPreview) {
            return descriptionPreview;
        }
        // No preview content available
        return null;
    }
    /**
     * Gets the content link with the highest preview priority (lowest previewPriority string value)
     * for display in previews. Based on legacy TaskDetails.getReferenceWithLowestPriority() logic.
     * @returns The content link to display, or null if no suitable links exist
     */ getContentLinkForPreview() {
        if (!this.links || this.links.length === 0) {
            return null;
        }
        // If there's only one link, return it
        if (this.links.length === 1) {
            return this.links[0];
        }
        // Find the link with the lowest previewPriority string value
        // If no previewPriority is set, treat it as lowest priority (empty string)
        return this.links.reduce((lowest, current)=>{
            if (!lowest) {
                return current;
            }
            const currentPriority = current.extensions?.previewPriority || "";
            const lowestPriority = lowest.extensions?.previewPriority || "";
            // String comparison for preview priority - lower string value = higher priority
            return currentPriority < lowestPriority ? current : lowest;
        }, null);
    }
    constructor(propertyBag){
        super(propertyBag);
        this.archivalInfo = {
            isArchived: propertyBag.archivalInfo?.isArchived ?? false,
            justification: propertyBag.archivalInfo?.justification ?? null,
            modifiedBy: propertyBag.archivalInfo?.modifiedBy ?? null,
            modifiedDate: propertyBag.archivalInfo?.modifiedDate ? Temporal.PlainDate.from(propertyBag.archivalInfo.modifiedDate) : null
        };
        this.userAssignments = propertyBag.userAssignments ?? [];
        this.bucketId = propertyBag.bucketId ?? null;
        this.checklist = propertyBag.checklist ?? [];
        this.capabilities = propertyBag.capabilities ?? {
            entityExtensions: false
        };
        this.creationInfo = propertyBag.creationInfo ?? {
            createdBy: propertyBag.lastModifiedBy,
            createdDateTime: Temporal.Now.instant(),
            externalId: null,
            externalVersion: null,
            scenarioId: null,
            scope: null,
            externalUrl: null
        };
        this.customFieldValues = propertyBag.customFieldValues ?? [];
        this.dependencies = propertyBag.dependencies ?? [];
        this.displayName = propertyBag.displayName;
        this.dueDateTime = propertyBag.dueDateTime ?? null;
        this.extensions = propertyBag.extensions ?? {
            conversationThreadId: null
        };
        this.finishDateTime = propertyBag.finishDateTime ?? null;
        this.goalIds = propertyBag.goalIds ?? [];
        this.hasChildren = propertyBag.hasChildren ?? false;
        this.hasChat = propertyBag.hasChat ?? false;
        this.kind = propertyBag.kind ?? TaskKind.Planner;
        this.lastModifiedBy = propertyBag.lastModifiedBy ?? propertyBag.lastModifiedBy;
        this.lastModifiedDateTime = propertyBag.lastModifiedDateTime ?? Temporal.Now.instant();
        this.links = propertyBag.links ?? [];
        this.notes = propertyBag.notes ? {
            content: propertyBag.notes.content,
            contentType: ContentType.HTML
        } : null;
        this.parentTaskId = propertyBag.parentTaskId ?? null;
        this.permissionSetId = propertyBag.permissionSetId ?? "";
        this.planId = propertyBag.planId;
        this.priority = propertyBag.priority ?? ConvertPriorityNameToPriority(PriorityName.Medium);
        this.personalView = propertyBag.personalView ?? {
            isOnMyDay: false,
            orderHints: new TaskPersonalViewOrderHints()
        };
        this.planView = propertyBag.planView ?? {
            appliedCategoryIds: [],
            previewType: TaskPreviewType.Automatic,
            orderHints: new TaskPlanViewOrderHints()
        };
        this.startDateTime = propertyBag.startDateTime ?? null;
        this.percentComplete = propertyBag.percentComplete ?? 0;
        this.completedBy = propertyBag.completedBy ?? null;
        this.completedDateTime = propertyBag.completedDateTime ?? null;
        this.recurrence = propertyBag.recurrence ?? null;
        this.timeZone = propertyBag.timeZone ?? null;
    }
}
// Content link type filters for common filtering scenarios
Task.DisplayableContentLinkTypes = [
    ContentLinkType.DocumentReference,
    ContentLinkType.WebReference,
    ContentLinkType.Form
];
Task.PreviewableContentLinkTypes = [
    ContentLinkType.DocumentReference,
    ContentLinkType.WebReference
];
